import { Upload, X } from "lucide-react";
import type React from "react";
import { useCallback, useRef, useState } from "react";
import { useProject } from "@/features/project";
import { useUI } from "@/features/ui";
import { AssetCard } from "./AssetCard";

// Check if asset matches accept patterns
const matchesAccept = (mimeType: string, accept?: string[]): boolean => {
  if (!accept || accept.length === 0) return true;
  return accept.some((pattern) => {
    const regex = new RegExp(`^${pattern.replace("*", ".*")}$`);
    return regex.test(mimeType);
  });
};

export const AssetsPanel: React.FC = () => {
  const { files, assetUrlMap, uploadAssetFiles, deleteAsset, renameAsset } = useProject();
  const { assetSelectionRequest, cancelAssetSelection, completeAssetSelection } = useUI();

  const assets = files.assets || [];
  const isSelectionMode = assetSelectionRequest !== null;

  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounterRef = useRef(0);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      dragCounterRef.current = 0;

      const dropFiles = e.dataTransfer.files;
      if (dropFiles && dropFiles.length > 0) {
        setIsUploading(true);
        try {
          await uploadAssetFiles(dropFiles);
        } finally {
          setIsUploading(false);
        }
      }
    },
    [uploadAssetFiles],
  );

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectFiles = e.target.files;
    if (selectFiles && selectFiles.length > 0) {
      setIsUploading(true);
      try {
        const uploadedAssets = await uploadAssetFiles(selectFiles);
        // If in selection mode and only one file was uploaded, auto-select it
        if (isSelectionMode && uploadedAssets && uploadedAssets.length === 1) {
          const uploadedAsset = uploadedAssets[0];
          if (matchesAccept(uploadedAsset.mimeType, assetSelectionRequest?.accept)) {
            completeAssetSelection(uploadedAsset.id);
          }
        }
      } finally {
        setIsUploading(false);
      }
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSelectClick = () => {
    fileInputRef.current?.click();
  };

  const handleAssetSelect = (assetId: string) => {
    completeAssetSelection(assetId);
  };

  return (
    <div
      className="absolute inset-0 flex flex-col bg-slate-900 overflow-hidden"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Selection mode header */}
      {isSelectionMode && (
        <div className="flex items-center justify-between px-4 py-3 bg-indigo-900/50 border-b border-indigo-700">
          <div className="flex items-center gap-2">
            <span className="text-xs text-indigo-300">Select Asset:</span>
            <span className="text-sm text-slate-200">{assetSelectionRequest.fieldLabel}</span>
          </div>
          <button
            onClick={cancelAssetSelection}
            className="flex items-center gap-1 px-2 py-1 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded text-xs transition-colors"
          >
            <X size={12} />
            Cancel
          </button>
        </div>
      )}

      {/* Drag overlay */}
      {isDragging && (
        <div className="absolute inset-0 bg-indigo-600/20 border-2 border-dashed border-indigo-500 z-50 flex items-center justify-center">
          <div className="bg-slate-900/90 px-6 py-4 rounded-lg flex flex-col items-center gap-2">
            <Upload size={32} className="text-indigo-400" />
            <p className="text-sm text-slate-200">Release to upload files</p>
          </div>
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={assetSelectionRequest?.accept?.join(",")}
        onChange={handleFileSelect}
        className="hidden"
      />

      {assets.length === 0 ? (
        /* Empty state - full panel drop zone */
        <div className="flex-1 flex flex-col items-center justify-center gap-4 p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-slate-800 flex items-center justify-center">
            <Upload size={32} className="text-slate-400" />
          </div>
          <div>
            <p className="text-sm text-slate-300 mb-1">Drag and drop files here to upload</p>
            <p className="text-xs text-slate-500">Supports all file formats</p>
          </div>
          <button
            onClick={handleSelectClick}
            disabled={isUploading}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-md text-sm transition-colors disabled:opacity-50"
          >
            <Upload size={16} />
            Upload Files
          </button>
        </div>
      ) : (
        /* Has assets - show grid with upload button */
        <div className="flex-1 overflow-auto p-4">
          {/* Header with upload button */}
          <div className="flex items-center justify-between mb-4">
            <p className="text-xs text-slate-500">{assets.length} assets</p>
            <button
              onClick={handleSelectClick}
              disabled={isUploading}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-md text-xs border border-slate-600 transition-colors disabled:opacity-50"
            >
              <Upload size={14} />
              Upload
            </button>
          </div>

          {/* Assets grid - auto-fill with min 200px width */}
          <div
            className="grid gap-4"
            style={{ gridTemplateColumns: "repeat(auto-fill, minmax(270px, 1fr))" }}
          >
            {assets.map((asset) => {
              const selectable = matchesAccept(asset.mimeType, assetSelectionRequest?.accept);
              return (
                <AssetCard
                  key={asset.id}
                  asset={asset}
                  assetUrl={assetUrlMap[asset.id]}
                  onDelete={deleteAsset}
                  onRename={renameAsset}
                  selectionMode={isSelectionMode}
                  selectable={selectable}
                  onSelect={handleAssetSelect}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
